import { BookingCarSelectionButton } from '@components/BookingFlow/Common/Car/BookingCarSelectionButton';
import { TemporarilyUnavailableModal } from '@components/BookingFlow/Mobile/salesItems/TemporarilyUnavailableModal';
import { UnavailableModal } from '@components/BookingFlow/Mobile/salesItems/UnavailableModal';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  FleetCarWithSalesItemProperties,
  SALES_ITEM_BOOKING_FLOW_TYPE,
  ServiceItemsMappedForFleetWithCars
} from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiDialog, NoddiSkeleton } from 'noddi-ui-web';
import { compareLicensePlates, formatCurrencyAmount } from 'noddi-util';
import {
  commonTrackingEvents,
  tracking,
  useBookingActions,
  useBookingCars,
  useBookingSelectedSalesItemsCars
} from 'noddi-web';
import { Fragment, useState } from 'react';
import { useGetUnavailableSalesItems } from '../../../../hooks/useGetUnavailableSalesItems';
import { getPriceFormatted } from '../../../../utils/translation';

type SalesItemProps = {
  salesItem: ServiceItemsMappedForFleetWithCars;
  type: SALES_ITEM_BOOKING_FLOW_TYPE;
  isWrappedInCollapsibleCard?: boolean;
};

export const SalesItemMultipleCars = ({ salesItem, type, isWrappedInCollapsibleCard }: SalesItemProps) => {
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();

  const selectedSalesItemsCarsCurrentItem = selectedSalesItemsCars.filter((item) =>
    salesItem.cars.some((car) => car.salesItemId === item.salesItemId)
  );

  const hasAnySelectedSalesItems = !!selectedSalesItemsCarsCurrentItem.length;

  const [isExpanded, setIsExpanded] = useState(hasAnySelectedSalesItems);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [showTemporarilyUnavailableModal, setShowTemporarilyUnavailableModal] = useState(false);

  const [showFullDescription, setShowFullDescription] = useState(false);

  const { addSalesItems, removeSalesItems, removeSalesItem, addSalesItem, removeCar } = useBookingActions();
  const bookingCars = useBookingCars();

  const { isUnavailableSalesItemsLoading, unavailableSalesItems } = useGetUnavailableSalesItems();

  const { name, shortDescription, description, price } = salesItem;

  const isAnySalesItemsUnavailable = salesItem.cars.some((car) =>
    unavailableSalesItems?.some((unavailableSalesItem) => unavailableSalesItem.salesItemId === car.salesItemId)
  );

  const temporarilyUnavailableCars = salesItem.cars.filter(
    (car) =>
      car.temporarilyUnavailable?.disabled &&
      bookingCars.some((bookingCar) => compareLicensePlates(bookingCar.licensePlate, car.licensePlate))
  );
  const isAnyCarTemporarilyUnavailable = temporarilyUnavailableCars.length > 0;
  const firstTemporarilyUnavailableReason = temporarilyUnavailableCars[0]?.temporarilyUnavailable;
  const affectedBookingCars = temporarilyUnavailableCars
    .map((car) => bookingCars.find((c) => compareLicensePlates(c.licensePlate, car.licensePlate)))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  // Compute header price: if all car prices equal, show just that price; otherwise show from <min>
  const carPrices = salesItem.cars.map((c) => c.price.amount).filter((a) => a > 0);
  const minCarPrice = carPrices.length ? Math.min(...carPrices) : price.amount;

  const isFree = minCarPrice === 0;
  const headerPriceFormatted = isFree ? t`Free` : getPriceFormatted([salesItem]);

  const onShowFullDescriptionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    tracking.track(commonTrackingEvents.salesItemDescriptionDetailsClicked, { salesItemName: name });
    setShowFullDescription(true);
  };

  function onExpandAndSelectAll(e: React.MouseEvent<HTMLButtonElement>) {
    const params = salesItem.cars.map(
      ({
        licensePlate,
        salesItemId,
        isAddonsAvailable,
        isWheelPickupRequired,
        isIncomingTireSeasonRequired,
        mutuallyExclusiveSalesItemIds,
        bookingCategoryType
      }) => ({
        licensePlate,
        salesItemId,
        type,
        isAddonsAvailable,
        isWheelPickupRequired,
        isIncomingTireSeasonRequired,
        mutuallyExclusiveSalesItemIds,
        bookingCategoryType
      })
    );

    if (hasAnySelectedSalesItems) {
      removeSalesItems(params);
      setIsExpanded(false);
    } else {
      addSalesItems(params);
      setIsExpanded(true);
    }

    e.stopPropagation();
  }

  function onSelect({
    e,
    car,
    isAlreadySelected
  }: {
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>;
    car: FleetCarWithSalesItemProperties;
    isAlreadySelected: boolean;
  }) {
    isAlreadySelected
      ? removeSalesItem({
          licensePlate: car.licensePlate,
          salesItemId: car.salesItemId,
          type
        })
      : addSalesItem({
          licensePlate: car.licensePlate,
          salesItemId: car.salesItemId,
          type,
          mutuallyExclusiveSalesItemIds: car.mutuallyExclusiveSalesItemIds,
          isAddonsAvailable: car.isAddonsAvailable,
          isWheelPickupRequired: car.isWheelPickupRequired,
          isIncomingTireSeasonRequired: car.isIncomingTireSeasonRequired,
          bookingCategoryType: car.bookingCategoryType
        });
    e.stopPropagation();
  }

  function onUnavailableModalOpen(e: React.MouseEvent<HTMLButtonElement>) {
    setShowUnavailableModal(true);
    e.stopPropagation();
  }

  function onTemporarilyUnavailableModalOpen(e: React.MouseEvent<HTMLButtonElement>) {
    setShowTemporarilyUnavailableModal(true);
    e.stopPropagation();
  }

  const showSkeletonLoader = isUnavailableSalesItemsLoading && !hasAnySelectedSalesItems;

  return (
    <>
      <UnavailableModal
        // Todo, now it shows first only
        salesItem={{ name: salesItem.name, salesItemId: salesItem.cars[0]?.salesItemId as number }}
        open={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
        unavailableSalesItems={unavailableSalesItems}
      />
      {!!firstTemporarilyUnavailableReason && (
        <TemporarilyUnavailableModal
          open={showTemporarilyUnavailableModal}
          onClose={() => setShowTemporarilyUnavailableModal(false)}
          reason={firstTemporarilyUnavailableReason}
          affectedCars={affectedBookingCars}
          onRemoveCar={removeCar}
        />
      )}
      <NoddiDialog title={name} onClose={() => setShowFullDescription(false)} open={showFullDescription}>
        {description}
      </NoddiDialog>
      <NoddiBasicCard
        separateAction
        className={cn(
          'flex flex-col gap-4 transition-all duration-200 ease-out',
          isWrappedInCollapsibleCard && 'border-none px-4 py-3 shadow-none! sm:p-5'
        )}
        disableActiveScale={!!isWrappedInCollapsibleCard}
        accessibleLabel={headerPriceFormatted ? t`${name}, ${headerPriceFormatted}` : name}
        onClick={() => {
          if (isAnyCarTemporarilyUnavailable) {
            setShowTemporarilyUnavailableModal(true);
            return;
          }
          if (!isAnySalesItemsUnavailable) {
            setIsExpanded((prev) => !prev);
          }
        }}
      >
        <div className={cn('flex flex-col gap-2')}>
          <div className='flex items-baseline justify-between gap-2'>
            <span className='text-lg font-medium'>{name}</span>

            {!isAnySalesItemsUnavailable && !isAnyCarTemporarilyUnavailable && (
              <div className={cn('transition-transform duration-300 ease-out', isExpanded && 'rotate-180')}>
                <NoddiIcon name='ChevronDown' />
              </div>
            )}
          </div>
          <p className={cn('font-semibold', isFree && 'text-successDark')}>{headerPriceFormatted}</p>
          <p>{shortDescription}</p>
          <div className='flex h-9 items-end justify-end'>
            {showSkeletonLoader ? (
              <div className='flex gap-2'>
                <NoddiSkeleton variant='button' className='h-9 w-[90px]' />
                <NoddiSkeleton variant='button' className='h-9 w-25' />
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                {isAnyCarTemporarilyUnavailable ? (
                  // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
                  <NoddiButton size='small' variant='link' onClick={onTemporarilyUnavailableModalOpen}>
                    ⚠️ <Trans>This service is temporarily unavailable</Trans>
                  </NoddiButton>
                ) : isAnySalesItemsUnavailable ? (
                  <NoddiButton
                    startIcon='SquareArrowOutUpRight'
                    size='small'
                    variant='link'
                    onClick={onUnavailableModalOpen}
                  >
                    <Trans>Why am I not able to select this?</Trans>
                  </NoddiButton>
                ) : (
                  <>
                    <NoddiButton
                      startIcon='SquareArrowOutUpRight'
                      size='small'
                      variant='link'
                      onClick={onShowFullDescriptionClick}
                    >
                      <Trans>Details</Trans>
                    </NoddiButton>
                    <NoddiButton
                      variant='outline'
                      size='small'
                      startIcon={hasAnySelectedSalesItems ? 'Minus' : 'Plus'}
                      onClick={onExpandAndSelectAll}
                    >
                      {hasAnySelectedSalesItems ? `${t`Remove`} (${selectedSalesItemsCarsCurrentItem.length})` : t`Add`}
                    </NoddiButton>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className='flex flex-col gap-2'>
            {salesItem.cars.map((car) => {
              const isSelected = selectedSalesItemsCars.some(
                (item) => item.salesItemId === car.salesItemId && item.licensePlate.number === car.licensePlate.number
              );
              return (
                <Fragment key={car.licensePlate.number}>
                  {showSkeletonLoader ? (
                    <NoddiSkeleton className='h-9' />
                  ) : (
                    <>
                      {!isAnySalesItemsUnavailable && (
                        <BookingCarSelectionButton
                          car={
                            bookingCars.find((bookingCar) =>
                              compareLicensePlates(bookingCar.licensePlate, car.licensePlate)
                            ) ?? car
                          }
                          isSelected={isSelected}
                          onClick={(event) => onSelect({ e: event, car, isAlreadySelected: isSelected })}
                          licensePlateNumber={car.licensePlate.number}
                          price={
                            car.price.amount === 0 ? t`Free` : formatCurrencyAmount({ money: car.price, decimals: 0 })
                          }
                        />
                      )}
                    </>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
      </NoddiBasicCard>
    </>
  );
};
